import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  EntityAcl,
  InsertEntityAclBody,
  SearchEntityAclParameters,
  UpdateEntityAclBody,
} from "./types/entityAcl";

const BASE_URL = "/admin/entity_acl";

export const useEntityAclApi = () => {
  const api = useApi();

  const mutateEntityAcl = useCallback(() => {
    mutateCache([BASE_URL]);
  }, []);

  const buildSearchUrl = useCallback((parameters: SearchEntityAclParameters, query: PaginationQuery) => {
    const url = buildUrl(BASE_URL, query);

    const params = new URLSearchParams();
    parameters.entityType && params.set("entityType", parameters.entityType);
    parameters.entityId && params.set("entityId", parameters.entityId);
    parameters.userId && params.set("userId", parameters.userId);
    parameters.permission && params.set("permission", parameters.permission);

    const queryString = params.toString();
    if (!queryString) {
      return url;
    }

    return `${url}${url.includes("?") ? "&" : "?"}${queryString}`;
  }, []);

  const useSearchEntityAcl = useCallback(
    (parameters: SearchEntityAclParameters, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildSearchUrl(parameters, query);
      return useSWR<Paginated<EntityAcl>, CustomError, string>(url, (url) =>
        api.get<Paginated<EntityAcl>>({ url, signal }),
      );
    },
    [api, buildSearchUrl],
  );

  const insertEntityAcl = useCallback(
    async (body: InsertEntityAclBody): Promise<void> => {
      await api.post<EntityAcl, InsertEntityAclBody>(BASE_URL, body);
      mutateEntityAcl();
    },
    [api, mutateEntityAcl],
  );

  const updateEntityAcl = useCallback(
    async (entityAclId: string, body: UpdateEntityAclBody): Promise<void> => {
      await api.put<UpdateEntityAclBody>(`${BASE_URL}/${entityAclId}`, body);
      mutateEntityAcl();
    },
    [api, mutateEntityAcl],
  );

  const deleteEntityAcl = useCallback(
    async (entityAclId: string): Promise<void> => {
      await api.del(`${BASE_URL}/${entityAclId}`);
      mutateEntityAcl();
    },
    [api, mutateEntityAcl],
  );

  return {
    useSearchEntityAcl,
    insertEntityAcl,
    updateEntityAcl,
    deleteEntityAcl,
  };
};
